import { useCallback, useEffect, useRef } from 'react';
import type { ComboBoxPrediction, IsMulti, Location } from '~/utilities';
import { ActionMeta, StylesConfig, SelectInstance } from 'react-select';
import AsyncSelect from 'react-select/async';
import {
  defaultMapLocation,
  inputBaseStyles,
  calculateBoundingBox,
} from '~/utilities';
import { NoOptionsMessage } from './components';
import { useAutocompleteService } from '~/hooks';
import { useAppSelector, useAppDispatch } from '~/state/hooks';
import { selectLocation, setLocation } from '~/state/location';
import { setMapBounds, setMapInteractionActive } from '~/state/mapData';
import { clearSelectedClinic } from '~/state/activeSelection';
import { hasFeatureFlag } from '@janeapp/feature-flags-client';

const styles: StylesConfig<ComboBoxPrediction, IsMulti> = {
  menuList: (base) => ({
    ...base,
    overflow: 'hidden',
    paddingTop: 0,
    paddingBottom: 0,
  }),
};

type LocationInputProps = {
  inputId?: string;
  isDisabled: boolean;
};

export const LOCATION_INPUT_ID = 'location-input';

export function LocationInput({ inputId, isDisabled }: LocationInputProps) {
  const ref = useRef<SelectInstance<ComboBoxPrediction, IsMulti>>(null);
  const location = useAppSelector(selectLocation);
  const dispatch = useAppDispatch();

  const { loadPredictionsDebounced, error, setError, isLoading } =
    useAutocompleteService();

  //listen for backspaces to clear errors
  // without making the input a controlled component
  useEffect(() => {
    const selectElement = ref.current?.inputRef;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Backspace' && error !== null) {
        setError(null);
      }
    };

    selectElement?.addEventListener('keydown', handleKeyDown);

    return () => {
      selectElement?.removeEventListener('keydown', handleKeyDown);
    };
  }, [error, setError]);

  const handleChange = useCallback(
    (
      selection: ComboBoxPrediction | null,
      { action }: ActionMeta<ComboBoxPrediction | null>
    ) => {
      error && setError(null);

      if (action === 'select-option' && selection?.geocoded) {
        const locationData: Location = {
          coordinates: selection.geocoded.coordinates,
          value: selection.label,
        };

        dispatch(setLocation(locationData));
        dispatch(
          setMapBounds(calculateBoundingBox(selection.geocoded.coordinates, 5))
        );
        dispatch(setMapInteractionActive(false));
        dispatch(clearSelectedClinic());
        return;
      }
    },
    [error, setError, dispatch]
  );

  const getDefaultValue = () => {
    if (hasFeatureFlag('NORTH_VANCOUVER_BETA')) {
      return location?.value
        ? {
            label: location?.value,
            value: location?.value,
          }
        : null; // Use null instead of undefined for React Select, undefined does not reset on browser back event
    } else {
      return {
        label: defaultMapLocation.value,
        value: defaultMapLocation.value,
      };
    }
  };

  return (
    <AsyncSelect
      inputId={inputId || LOCATION_INPUT_ID}
      ref={ref}
      isClearable
      blurInputOnSelect
      defaultOptions
      value={getDefaultValue()}
      closeMenuOnSelect
      placeholder="Address, neighbourhood, or city"
      isDisabled={isLoading || isDisabled}
      styles={{ ...inputBaseStyles, ...styles }}
      onChange={handleChange}
      aria-label="Search for your location by address, neighbourhood, or city"
      className="w-full"
      loadOptions={loadPredictionsDebounced}
      maxMenuHeight={600}
      components={{
        LoadingIndicator: () => null,
        IndicatorSeparator: () => null,
        DropdownIndicator: () => null,
        LoadingMessage: () => null,
        ClearIndicator: () => null,
        NoOptionsMessage: (props) =>
          /*This component appears inside the dropdown menu, so using it for error messaging*/
          error ? <NoOptionsMessage {...props} error={error} /> : null,
      }}
    />
  );
}
